import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dumbbell, Plus, Trash2, Camera, Upload, Flame, Heart } from "lucide-react";
import { useState, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const SECTION_OPTIONS = [
  { id: "warmup", label: "Aufwärmen", icon: "🔥", color: "bg-amber-100 text-amber-700 border-amber-300" },
  { id: "main", label: "Haupttraining", icon: "💪", color: "bg-blue-100 text-blue-700 border-blue-300" },
  { id: "cooldown", label: "Dehnung", icon: "🧘", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
];

const EQUIPMENT_TYPES = [
  { id: "langhantel", label: "Langhantel" },
  { id: "kurzhanteln", label: "Kurzhanteln" },
  { id: "kurzhantel", label: "Kurzhantel" },
  { id: "maschine", label: "Maschine" },
  { id: "kabelzug", label: "Kabelzug" },
  { id: "eigengewicht", label: "Eigengewicht" },
  { id: "sz-stange", label: "SZ-Stange" },
];

export default function AddExerciseDialog({
  open, onOpenChange, exerciseFilter, setExerciseFilter,
  muscleGroups, filteredExercises, getMuscleLabel, onAddExercise, onRefreshDB,
  equipmentFilter, setEquipmentFilter,
  addToSection, setAddToSection,
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newMuscle, setNewMuscle] = useState("");
  const [newEquipment, setNewEquipment] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [newSection, setNewSection] = useState("main");
  const [creating, setCreating] = useState(false);
  const imageInputRef = useRef(null);

  const handleImageSelect = (file) => {
    if (!file) return;
    setNewImage(file);
    setNewImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`${API}/exercises/upload-image`, formData, {
      headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
    });
    return `${API}/files/${res.data.path}`;
  };

  const handleCreate = async () => {
    if (!newName.trim() || !newMuscle) {
      toast.error("Name und Muskelgruppe erforderlich");
      return;
    }
    setCreating(true);
    try {
      let imageUrl = undefined;
      if (newImage) {
        imageUrl = await uploadImage(newImage);
      }
      const body = {
        name: newName.trim(),
        muscle: newMuscle,
        equipment: newEquipment.trim() || "Sonstiges",
        description: newDesc.trim(),
      };
      if (imageUrl) body.image = imageUrl;
      const res = await axios.post(`${API}/exercises/database`, body, { headers: getHeaders() });
      toast.success(`"${res.data.name}" erstellt`);
      // Set section so when user adds from list, it goes to the right section
      if (setAddToSection) setAddToSection(newSection);
      resetCreateForm();
      if (onRefreshDB) onRefreshDB();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Erstellen");
    }
    setCreating(false);
  };

  const resetCreateForm = () => {
    setNewName(""); setNewMuscle(""); setNewEquipment(""); setNewDesc("");
    setNewImage(null); setNewImagePreview(null); setNewSection("main"); setShowCreate(false);
  };

  const handleDelete = async (e, ex) => {
    e.stopPropagation();
    if (!ex.custom) {
      toast.error("Standard-Übungen können nicht gelöscht werden");
      return;
    }
    try {
      await axios.delete(`${API}/exercises/database/${ex.id}`, { headers: getHeaders() });
      toast.success(`"${ex.name}" gelöscht`);
      if (onRefreshDB) onRefreshDB();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) resetCreateForm(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dumbbell className="w-5 h-5 text-amber-500" /> Übungsdatenbank
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">Übung hinzufügen, erstellen oder aus der Datenbank entfernen</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          <Input
            placeholder="Übung suchen..."
            value={exerciseFilter}
            onChange={e => setExerciseFilter(e.target.value)}
            className="flex-1"
            data-testid="exercise-search-input"
          />
          <Button size="sm" variant={showCreate ? "secondary" : "default"} onClick={() => setShowCreate(!showCreate)} data-testid="toggle-create-exercise">
            <Plus className="w-4 h-4 mr-1" /> Neue Übung
          </Button>
        </div>

        {/* Section selector - where to add exercise */}
        <div className="flex items-center gap-1.5 mb-2" data-testid="section-selector">
          <span className="text-[10px] text-slate-400 font-semibold uppercase mr-1">Hinzufügen zu:</span>
          {SECTION_OPTIONS.map(s => (
            <button
              key={s.id}
              onClick={() => setAddToSection(s.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${
                addToSection === s.id ? s.color + ' ring-1 ring-offset-1' : 'bg-slate-50 text-slate-500 border-slate-200'
              }`}
              data-testid={`section-select-${s.id}`}
            >
              {s.icon} {s.label}
            </button>
          ))}
        </div>

        {showCreate && (
          <div className="border border-blue-200 rounded-lg p-3 mb-2 bg-blue-50/50 space-y-2" data-testid="create-exercise-form">
            <div className="flex gap-3">
              {/* Image upload area */}
              <div
                className="w-20 h-20 flex-shrink-0 rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-400 cursor-pointer flex items-center justify-center bg-white overflow-hidden transition-colors"
                onClick={() => imageInputRef.current?.click()}
                data-testid="new-exercise-image-btn"
              >
                {newImagePreview ? (
                  <img src={newImagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center gap-1 text-slate-400">
                    <Camera className="w-5 h-5" />
                    <span className="text-[9px]">Bild</span>
                  </div>
                )}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={e => { handleImageSelect(e.target.files[0]); e.target.value = ""; }}
                  data-testid="new-exercise-image-input"
                />
              </div>
              <div className="flex-1 space-y-2">
                <Input placeholder="Übungsname *" value={newName} onChange={e => setNewName(e.target.value)} data-testid="new-exercise-name" />
                <Input placeholder="Equipment (optional)" value={newEquipment} onChange={e => setNewEquipment(e.target.value)} data-testid="new-exercise-equipment" />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {muscleGroups.map(mg => (
                <button
                  key={mg.id}
                  onClick={() => setNewMuscle(mg.id)}
                  className={`px-2 py-1 rounded text-xs font-medium ${newMuscle === mg.id ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
                  data-testid={`new-muscle-${mg.id}`}
                >
                  {mg.name}
                </button>
              ))}
            </div>
            <textarea
              placeholder="Beschreibung (optional)"
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              className="w-full text-sm p-2 border border-slate-200 rounded-md resize-y min-h-[50px] focus:outline-none focus:border-blue-400"
              rows={2}
              data-testid="new-exercise-desc"
            />
            {/* Section for new exercise */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-500 font-semibold">Sektion:</span>
              {SECTION_OPTIONS.map(s => (
                <button key={s.id} onClick={() => setNewSection(s.id)}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium border ${newSection === s.id ? s.color : 'bg-white text-slate-500 border-slate-200'}`}
                  data-testid={`new-section-${s.id}`}
                >{s.icon} {s.label}</button>
              ))}
            </div>
            <Button size="sm" onClick={handleCreate} disabled={creating} className="w-full" data-testid="submit-new-exercise">
              {creating ? "Erstelle..." : "Übung erstellen"}
            </Button>
          </div>
        )}

        {/* Muscle group filter */}
        <div className="flex flex-wrap gap-1.5 mb-1">
          <span className="text-[10px] text-slate-400 font-semibold uppercase self-center mr-1">Muskel:</span>
          <button
            onClick={() => setExerciseFilter("")}
            className={`px-2 py-1 rounded text-xs font-medium ${!exerciseFilter ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}
          >
            Alle
          </button>
          {muscleGroups.map(mg => (
            <button
              key={mg.id}
              onClick={() => setExerciseFilter(mg.id)}
              className={`px-2 py-1 rounded text-xs font-medium ${exerciseFilter === mg.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}
            >
              {mg.name}
            </button>
          ))}
        </div>

        {/* Equipment filter */}
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="equipment-filter">
          <span className="text-[10px] text-slate-400 font-semibold uppercase self-center mr-1">Equipment:</span>
          <button
            onClick={() => setEquipmentFilter("")}
            className={`px-2 py-0.5 rounded text-[11px] font-medium ${!equipmentFilter ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}
            data-testid="equipment-filter-all"
          >
            Alle
          </button>
          {EQUIPMENT_TYPES.map(eq => (
            <button
              key={eq.id}
              onClick={() => setEquipmentFilter(eq.id)}
              className={`px-2 py-0.5 rounded text-[11px] font-medium ${equipmentFilter === eq.id ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}
              data-testid={`equipment-filter-${eq.id}`}
            >
              {eq.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-auto space-y-2">
          {filteredExercises.map(ex => (
            <div
              key={ex.id}
              className="flex items-center gap-3 p-2 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer group"
              onClick={() => onAddExercise(ex)}
              data-testid={`db-exercise-${ex.id}`}
            >
              <img src={ex.image} alt={ex.name} className="w-14 h-14 rounded-md object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-semibold text-slate-900">{ex.name}</p>
                  {ex.custom && <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-600 rounded font-medium">Custom</span>}
                </div>
                <p className="text-xs text-slate-500">{getMuscleLabel(ex.muscle)} • {ex.equipment}</p>
              </div>
              {ex.custom ? (
                <button
                  onClick={(e) => handleDelete(e, ex)}
                  className="p-1.5 rounded hover:bg-red-50 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  title="Übung löschen"
                  data-testid={`delete-db-exercise-${ex.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              ) : (
                <Plus className="w-5 h-5 text-emerald-500 flex-shrink-0" />
              )}
            </div>
          ))}
          {filteredExercises.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-8">Keine Übungen gefunden</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
